import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Clock3, MapPin, Smartphone } from "lucide-react";
import { api } from "@/lib/api";
import { todayISO, fmtDayLong } from "@/lib/format";
import { PageHeader, Panel, Segmented, Avatar, KV, ErrorState } from "@/components/drift/common";
import { OrderStatus } from "@/components/drift/StatusBadge";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { PhoneFrame, PhoneAppBar } from "@/components/drift/PhoneFrame";
import { MontorFeed } from "@/components/drift/MontorFeed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   /drift/felt – the technicians' phone view, as seen from the office.
   Pick a technician on the left; the handset shows their live day.
   ------------------------------------------------------------------ */

export default function Felt() {
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);
  const [theme, setTheme] = useState("light");
  const today = todayISO();

  const load = () => api.get(`/drift/planning?date=${today}`).then((r) => { setPlan(r.data); setErr(null); }).catch((e) => setErr(e));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => (plan?.rows || []).slice().sort((a, b) => b.orders.length - a.orders.length || a.user.sort - b.user.sort), [plan]);
  const selectedId = params.get("montor") || rows[0]?.user.id || "";
  const selected = rows.find((r) => r.user.id === selectedId) || rows[0];
  const select = (id) => setParams({ montor: id }, { replace: true });

  const estHours = selected ? selected.orders.reduce((s, o) => s + (o.estimated_hours || 0), 0) : 0;
  const times = selected ? selected.orders.map((o) => o.scheduled_time).filter(Boolean).sort() : [];
  const now = new Date();
  const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  if (err) return <ErrorState title="Fekk ikkje lasta feltvisinga" onRetry={load} />;

  return (
    <div data-testid="felt-page">
      <PageHeader
        eyebrow="Felt"
        title="Feltvising"
        subtitle="Slik ser montørane dagen sin på telefonen – same data som i Drift, i sanntid."
        testid="felt-header"
        actions={<>
          <Segmented size="sm" value={theme} onChange={setTheme} options={[["light", "Lys"], ["dark", "Mørk"]]} testid="felt-theme" />
          <Button asChild variant="outline" className="h-9 rounded-lg bg-card"><Link to="/drift/montor" data-testid="felt-open-montor"><Smartphone className="mr-1.5 h-4 w-4" strokeWidth={1.75} /> Opne montørvisning</Link></Button>
        </>}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        {/* Technicians */}
        <Panel title="Montørar" subtitle={fmtDayLong(today)} bodyClass="p-2" testid="felt-montorar">
          {!plan && <div className="space-y-1.5 p-1">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg bg-secondary" />)}</div>}
          {plan && (
            <ul className="grid grid-cols-1 gap-0.5">
              {rows.map((r) => {
                const on = r.user.id === selected?.user.id;
                const next = r.orders.find((o) => o.status !== "ferdig");
                return (
                  <li key={r.user.id} className="min-w-0">
                    <button type="button" onClick={() => select(r.user.id)} className={cn("relative flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", on ? "bg-accent/70" : "hover:bg-accent/40")} data-testid="felt-montor-row" aria-pressed={on}>
                      {on && <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-brand" aria-hidden />}
                      <Avatar name={r.user.name} initials={r.user.initials} tone={on ? "dark" : "soft"} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-medium">{r.user.name}</span>
                        <span className="block truncate text-[12px] text-muted-foreground">{r.user.title}</span>
                      </span>
                      <span className="text-right">
                        <span className="t-num block text-[15px] leading-none">{r.orders.length}</span>
                        <span className="mt-0.5 block font-mono text-[10.5px] text-muted-foreground">{next?.scheduled_time || (r.orders.length ? "–" : "ledig")}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* Phone + summary */}
        <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-start">
          <div className="flex min-w-0 justify-center overflow-hidden lg:justify-start" data-testid="felt-phone">
            <PhoneFrame dark={theme === "dark"} time={clock}>
              {selected ? (
                <>
                  <PhoneAppBar user={selected.user} />
                  <MontorFeed key={selected.user.id} assignedTo={selected.user.id} name={selected.user.name} embedded />
                </>
              ) : (
                <div className="p-5"><Skeleton className="h-6 w-40 rounded-md bg-secondary" /><Skeleton className="mt-3 h-10 w-56 rounded-md bg-secondary" /></div>
              )}
            </PhoneFrame>
          </div>

          {selected && (
            <div className="min-w-0 space-y-5" data-testid="felt-summary">
              <Panel title={`Dagen til ${selected.user.name.split(" ")[0]}`} subtitle={selected.user.title} testid="felt-day-panel">
                <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-border/70 [&>*]:bg-card">
                  <div className="p-3"><p className="text-[12px] text-muted-foreground">Oppdrag i dag</p><p className="t-num mt-1 text-2xl leading-none">{selected.orders.length}</p></div>
                  <div className="p-3"><p className="text-[12px] text-muted-foreground">Estimert</p><p className="t-num mt-1 text-2xl leading-none">{estHours || 0} <span className="text-[13px] font-normal text-muted-foreground">t</span></p></div>
                  <div className="p-3"><p className="text-[12px] text-muted-foreground">Første oppmøte</p><p className="t-num mt-1 text-2xl leading-none">{times[0] || "–"}</p></div>
                </div>
                <div className="mt-4 divide-y divide-border/60">
                  <KV k="Avdeling" v={selected.user.department} />
                  <KV k="Telefon" v={selected.user.phone} mono />
                  <KV k="Siste oppmøte" v={times[times.length - 1] || "–"} mono />
                </div>
              </Panel>

              <Panel title="Oppdraga i dag" subtitle={selected.orders.length ? "Trykk for å opne i kontorvisning" : "Ingen planlagde oppdrag"} bodyClass="p-2" testid="felt-orders-panel">
                {selected.orders.length === 0 && <p className="px-3 py-6 text-center text-[13px] text-muted-foreground">Ledig i dag. Tildel oppdrag i <Link to="/drift/planlegging" className="u-link font-medium text-foreground">planlegging</Link>.</p>}
                <ul className="grid grid-cols-1 gap-0.5">
                  {selected.orders.map((o) => (
                    <li key={o.id} className="min-w-0">
                      <Link to={`/drift/oppdrag/${o.id}`} className="group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors duration-150 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="felt-order-row">
                        <span className="w-[46px] shrink-0 font-mono text-[12.5px] text-muted-foreground tabular">{o.scheduled_time || "–"}</span>
                        <CompanyMark name={o.customer_name} brand={o.customer_brand} type={o.customer_type} size="sm" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-medium">{o.title}</span>
                          <span className="flex min-w-0 items-center gap-1 text-[12px] text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" strokeWidth={1.75} aria-hidden /><span className="truncate">{o.location_address || o.location_name}</span></span>
                        </span>
                        {o.estimated_hours ? <span className="hidden items-center gap-1 font-mono text-[11.5px] text-muted-foreground sm:inline-flex"><Clock3 className="h-3 w-3" strokeWidth={1.75} aria-hidden />{o.estimated_hours} t</span> : null}
                        <OrderStatus status={o.status} size="sm" />
                        <ArrowUpRight className="link-diag h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Panel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
